document.documentElement.classList.remove('no-js');

/* =========================================================
   Utilities
   ========================================================= */
const qs = (sel, ctx = document) => ctx.querySelector(sel);
const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

/* Localized UI strings (populated in theme.liquid via window.themeStrings) */

/* Shopify's `t` filter HTML-escapes the whole translation whenever it
   interpolates a value, so a string like
     "Code \"{{ code }}\" will be applied at checkout."
   arrives here as `Code &quot;[CODE]&quot; …`. `| json` preserves those
   entities verbatim, and textContent does not decode them — so the entity
   showed up literally on screen. Decode once, centrally, so every caller gets
   real text no matter which string gains a quote or apostrophe later.
   Anything written with innerHTML must still be run through escapeHTML. */
const decodeEntities = (str) => {
  if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
  return str
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');   // last, so "&amp;quot;" never becomes a quote
};

const T = (key, fallback) => decodeEntities((window.themeStrings && window.themeStrings[key]) || fallback);
const Tn = (key, fallback, values = {}) => {
  let s = T(key, fallback);
  Object.entries(values).forEach(([k, v]) => { s = s.replace(`[${k}]`, v); });
  return s;
};


async function fetchJSON(url, options = {}) {
  const isFormData = options.body instanceof FormData;
  const res = await fetch(url, {
    ...options,
    headers: isFormData
      ? { ...(options.headers || {}) }
      : { 'Content-Type': 'application/json', ...(options.headers || {}) }
  });
  if (!res.ok) {
    let message = 'Something went wrong. Please try again.';
    try {
      const data = await res.json();
      message = data.description || data.message || message;
    } catch (e) { /* noop */ }
    throw new Error(message);
  }
  return res.json();
}

function formatMoney(cents) {
  const amount = (cents / 100).toFixed(2);
  const format = window.themeMoneyFormat || '${{amount}}';
  return format.replace('{{amount}}', amount).replace('{{ amount }}', amount);
}

/* Toast — visible feedback + aria-live announcement */
const Toast = {
  el: null,
  _timer: null,
  init() { this.el = qs('#ThemeToast'); },
  show(message, isError = false) {
    if (!this.el) return;
    this.el.textContent = message;
    this.el.classList.toggle('is-error', isError);
    this.el.classList.add('is-visible');
    clearTimeout(this._timer);
    this._timer = setTimeout(() => this.el.classList.remove('is-visible'), 3200);
  }
};

/* Analytics — push ecommerce events for GTM/GA4 pickup */
function trackEvent(name, payload = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: name, ...payload });
}

/* Focus trap for overlays (cart drawer, quick view, mobile menu) */
const FocusTrap = {
  active: null,
  activate(container, returnTo) {
    this.release(false);
    const focusables = () => qsa(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      container
    ).filter(el => el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement);
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    this.active = { container, handler, returnTo: returnTo || document.activeElement };
    const target = focusables()[0];
    if (target) target.focus();
  },
  release(restoreFocus = true) {
    if (!this.active) return;
    this.active.container.removeEventListener('keydown', this.active.handler);
    const returnTo = this.active.returnTo;
    this.active = null;
    if (restoreFocus && returnTo && document.contains(returnTo)) returnTo.focus();
  }
};

/* =========================================================
   Cart Drawer
   ========================================================= */
const CartDrawer = {
  drawer: null,
  overlay: null,

  init() {
    this.drawer = qs('#CartDrawer');
    this.overlay = qs('#CartDrawerOverlay');
    if (!this.drawer) return;

    qsa('[data-cart-toggle]').forEach(btn => btn.addEventListener('click', () => this.open()));
    qsa('[data-cart-close]').forEach(btn => btn.addEventListener('click', () => this.close()));
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });

    this.bindLineItemControls(this.drawer);
    this.bindExtras();
  },

  isOpen() { return this.drawer?.classList.contains('is-open'); },

  open() {
    this.drawer?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    this.drawer?.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    if (this.drawer) FocusTrap.activate(this.drawer);
  },

  close() {
    this.drawer?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    this.drawer?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    FocusTrap.release();
  },

  /* Replace drawer contents from a rendered cart-drawer section (add.js?sections=) */
  applySection(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const newInner = doc.querySelector('.cart-drawer__inner');
    const currentInner = qs('.cart-drawer__inner', this.drawer);
    if (newInner && currentInner) {
      currentInner.replaceWith(newInner);
      this.bindLineItemControls(this.drawer);
      this.bindExtras();
    }
    const countMatch = qs('.cart-drawer__header .h3', this.drawer)?.textContent.match(/\((\d+)\)/);
    if (countMatch) this.updateCartCount(countMatch[1]);
  },

  bindExtras() {
    qsa('[data-cart-note]').forEach(area => {
      if (area.dataset.bound) return;
      area.dataset.bound = 'true';
      area.addEventListener('change', async () => {
        try {
          await fetchJSON('/cart/update.js', { method: 'POST', body: JSON.stringify({ note: area.value }) });
          Toast.show(T('noteSaved', 'Order note saved'));
        } catch (e) {
          Toast.show(T('noteError', 'Could not save the note'), true);
        }
      });
    });
    qsa('[data-discount-apply]').forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', async () => {
        const wrap = btn.closest('.cart-discount');
        const input = qs('[data-discount-input]', wrap);
        const msg = qs('[data-discount-msg]', wrap);
        const code = input?.value.trim();
        if (!code) return;
        try {
          await fetch(`/discount/${encodeURIComponent(code)}`);

          /* If the code belongs to a gift rule the cart has not reached yet,
             say what is missing instead of promising a discount that will
             quietly fail at checkout. */
          let shortfall = null;
          if (typeof GiftOffers !== 'undefined') {
            shortfall = await GiftOffers.messageForCode(code);
          }

          if (msg) {
            msg.hidden = false;
            msg.textContent = shortfall
              || Tn('discountApplied', `Code "${code}" will be applied at checkout.`, { CODE: code });
          }
          Toast.show(shortfall || T('discountToast', 'Discount will apply at checkout'), !!shortfall);

          // entering a code should feel immediate rather than waiting for the
          // next cart change
          if (typeof GiftOffers !== 'undefined') GiftOffers.check(code);
        } catch (e) {
          Toast.show(T('discountError', 'Could not apply the code'), true);
        }
      });
    });
  },

  async refresh() {
    try {
      const cart = await fetchJSON('/cart.js');
      this.updateCartCount(cart.item_count);

      const sectionRes = await fetch(`/?sections=cart-drawer`);
      if (sectionRes.ok) {
        const data = await sectionRes.json();
        if (data['cart-drawer']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(data['cart-drawer'], 'text/html');
          const newInner = doc.querySelector('.cart-drawer__inner');
          const currentInner = qs('.cart-drawer__inner', this.drawer);
          if (newInner && currentInner) currentInner.replaceWith(newInner);
          this.bindLineItemControls(this.drawer);
          this.bindExtras();
        }
      }
    } catch (err) {
      console.error('Cart refresh failed', err);
    }
    // the cart just changed, so re-evaluate the gift (it self-guards against
    // recursion, and does nothing when there is no gift configured)
    if (typeof GiftOffers !== 'undefined') GiftOffers.check();
  },

  updateCartCount(count) {
    qsa('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.setAttribute('data-cart-count', count);
    });
  },

  bindLineItemControls(root) {
    if (!root) return;
    qsa('[data-cart-qty-decrease]', root).forEach(btn => {
      btn.addEventListener('click', () => this.changeQuantity(btn, -1));
    });
    qsa('[data-cart-qty-increase]', root).forEach(btn => {
      btn.addEventListener('click', () => this.changeQuantity(btn, 1));
    });
    qsa('[data-cart-qty-input]', root).forEach(input => {
      input.addEventListener('change', () => this.setQuantity(input, parseInt(input.value, 10) || 0));
    });
    qsa('[data-cart-remove]', root).forEach(btn => {
      btn.addEventListener('click', () => {
        const item = btn.closest('[data-cart-item]');
        const line = item?.getAttribute('data-line');
        if (line) this.setLineQuantity(line, 0);
      });
    });
  },

  changeQuantity(btn, delta) {
    const wrapper = btn.closest('[data-cart-item]');
    const input = qs('[data-cart-qty-input]', wrapper);
    const line = wrapper?.getAttribute('data-line');
    const newQty = Math.max(0, (parseInt(input.value, 10) || 0) + delta);
    input.value = newQty;
    if (line) this.setLineQuantity(line, newQty);
  },

  setQuantity(input, value) {
    const wrapper = input.closest('[data-cart-item]');
    const line = wrapper?.getAttribute('data-line');
    if (line) this.setLineQuantity(line, Math.max(0, value));
  },

  async setLineQuantity(line, quantity) {
    try {
      const cart = await fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line: parseInt(line, 10), quantity })
      });
      if (typeof CardStepper !== 'undefined') CardStepper.applyCart(cart);
      const cartPage = qs('.cart-page');
      if (cartPage) {
        window.location.reload();
        return;
      }
      await this.refresh();
    } catch (err) {
      console.error('Failed to update cart', err);
    }
  }
};

/* =========================================================
   Mobile menu
   ========================================================= */
const MobileMenu = {
  init() {
    const toggle = qs('[data-menu-toggle]');
    const menu = qs('#MobileMenu');
    const overlay = qs('[data-menu-overlay]');
    if (!toggle || !menu) return;

    const setOpen = (isOpen) => {
      menu.hidden = !isOpen;
      menu.classList.toggle('is-open', isOpen);
      if (overlay) overlay.hidden = !isOpen;
      toggle.setAttribute('aria-expanded', String(isOpen));
      document.body.style.overflow = isOpen ? 'hidden' : '';
      if (isOpen) FocusTrap.activate(menu, toggle);
      else FocusTrap.release();
    };

    toggle.addEventListener('click', () => setOpen(!menu.classList.contains('is-open')));
    overlay?.addEventListener('click', () => setOpen(false));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) setOpen(false);
    });
  }
};

/* =========================================================
   Predictive search (suggest.json)
   ========================================================= */
const PredictiveSearch = {
  init() {
    const input = qs('[data-predictive-input]');
    const results = qs('[data-predictive-results]');
    if (!input || !results) return;

    let timer = null;
    let controller = null;

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) { results.hidden = true; results.innerHTML = ''; return; }
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const res = await fetch(
            `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`,
            { signal: controller.signal }
          );
          if (!res.ok) return;
          const data = await res.json();
          const products = data.resources?.results?.products || [];
          if (!products.length) {
            results.innerHTML = `<p class="predictive-results__empty">${escapeHTML(T('noMatches', 'No matching products'))}</p>`;
          } else {
            results.innerHTML = products.map(p => `
              <a href="${escapeHTML(p.url)}" class="predictive-result">
                ${p.featured_image && p.featured_image.url
                  ? `<img src="${escapeHTML(imageUrl(p.featured_image.url, 120))}" alt="" width="48" height="48" loading="lazy">`
                  : '<span class="predictive-result__noimg"></span>'}
                <span class="predictive-result__title">${escapeHTML(p.title)}</span>
                <span class="predictive-result__price">${escapeHTML(p.price || '')}</span>
              </a>`).join('') +
              `<a class="predictive-results__all" href="/search?q=${encodeURIComponent(q)}&type=product">${escapeHTML(T('seeAll', 'See all results →'))}</a>`;
          }
          results.hidden = false;
        } catch (e) { /* aborted or offline — leave as is */ }
      }, 250);
    });

    document.addEventListener('click', (e) => {
      if (!results.contains(e.target) && e.target !== input) results.hidden = true;
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { results.hidden = true; }
    });
  }
};

/* =========================================================
   Header search toggle
   ========================================================= */
const HeaderSearch = {
  init() {
    const toggle = qs('[data-search-toggle]');
    const search = qs('#HeaderSearch');
    if (!toggle || !search) return;

    toggle.addEventListener('click', () => {
      const isOpen = search.hidden;
      search.hidden = !isOpen;
      toggle.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) qs('input[type="search"]', search)?.focus();
    });
  }
};

/* =========================================================
   Collection filters toggle (mobile)
   ========================================================= */
const CollectionFilters = {
  init() {
    const toggle = qs('[data-filter-toggle]');
    const panel = qs('[data-filters]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      const isOpen = panel.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
    });

    const sortSelect = qs('[data-sort-select]');
    sortSelect?.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });

    const filterForm = qs('[data-filter-form]');
    if (filterForm) {
      qsa('input[type="checkbox"]', filterForm).forEach(input => {
        input.addEventListener('change', () => {
          if (filterForm.requestSubmit) filterForm.requestSubmit();
          else filterForm.submit();
        });
      });
      filterForm.addEventListener('submit', () => {
        // Drop empty fields so URLs stay clean
        qsa('input', filterForm).forEach(input => {
          if (input.value === '' || (input.type === 'checkbox' && !input.checked)) {
            input.disabled = true;
          }
        });
      });
    }
  }
};

/* Fade-swap a product image: fade out, load new src, fade back in */
function swapImage(img, url) {
  if (img.src === url) return;
  img.classList.add('is-fading');
  const loader = new Image();
  loader.onload = () => {
    img.removeAttribute('srcset');
    img.src = url;
    img.classList.remove('is-fading');
  };
  loader.onerror = () => img.classList.remove('is-fading');
  loader.src = url;
}

/* =========================================================
   Product info tabs
   ========================================================= */
const ProductTabs = {
  init() {
    qsa('[data-tabs]').forEach(tabs => {
      const buttons = qsa('[data-tab-target]', tabs);
      const panels = qsa('[data-tab-panel]', tabs);

      buttons.forEach(btn => {
        const target = btn.getAttribute('data-tab-target');
        btn.id = `tab-${target}`;
        btn.setAttribute('aria-controls', `panel-${target}`);
        btn.tabIndex = btn.classList.contains('is-active') ? 0 : -1;
      });
      panels.forEach(p => {
        const target = p.getAttribute('data-tab-panel');
        p.id = `panel-${target}`;
        p.setAttribute('aria-labelledby', `tab-${target}`);
        p.tabIndex = 0;
      });

      const activate = (btn) => {
        const target = btn.getAttribute('data-tab-target');
        buttons.forEach(b => {
          const active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-selected', String(active));
          b.tabIndex = active ? 0 : -1;
        });
        panels.forEach(p => {
          p.hidden = p.getAttribute('data-tab-panel') !== target;
        });
      };

      buttons.forEach((btn, i) => {
        btn.addEventListener('click', () => activate(btn));
        btn.addEventListener('keydown', (e) => {
          let next = null;
          if (e.key === 'ArrowRight') next = buttons[(i + 1) % buttons.length];
          if (e.key === 'ArrowLeft') next = buttons[(i - 1 + buttons.length) % buttons.length];
          if (e.key === 'Home') next = buttons[0];
          if (e.key === 'End') next = buttons[buttons.length - 1];
          if (next) { e.preventDefault(); next.focus(); activate(next); }
        });
      });
    });
  }
};

/* =========================================================
   Product form: variant selection, quantity, gallery, add to cart
   ========================================================= */
const ProductForm = {
  init() {
    qsa('#ProductForm').forEach(form => this.bindForm(form));
    this.bindGallery();
  },

  bindVariantSelection(form) {
    const dataScript = qs('[data-product-variants-json]', form.closest('.product-detail'));
    if (!dataScript) return;

    let variants;
    try {
      variants = JSON.parse(dataScript.textContent);
    } catch (e) {
      return;
    }

    const optionInputs = qsa('input[data-option-index]', form);
    if (!optionInputs.length) return;

    const variantIdInput = qs('[data-variant-id]', form);
    const submitBtn = qs('[data-add-to-cart]', form);
    const submitText = qs('[data-add-to-cart-text]', form);

    const getSelectedOptions = () => {
      const selected = [];
      optionInputs.forEach(input => {
        if (input.checked) selected[parseInt(input.dataset.optionIndex, 10)] = input.value;
      });
      return selected;
    };

    const applyVariant = (variant) => {
      if (!variant) {
        if (submitBtn) submitBtn.disabled = true;
        if (submitText) submitText.textContent = T('unavailable', 'Unavailable');
        return;
      }
      if (variantIdInput) variantIdInput.value = variant.id;
      if (submitBtn) submitBtn.disabled = !variant.available;
      if (submitText) {
        submitText.textContent = variant.available
          ? `${T('addToCart', 'Add to cart')} — ${formatMoney(variant.price)}`
          : T('soldOut', 'Sold out');
      }
      const buybarBtn = qs('[data-buybar-btn]');
      if (buybarBtn) {
        buybarBtn.disabled = !variant.available;
        buybarBtn.textContent = variant.available ? T('addToCart', 'Add to cart') : T('soldOut', 'Sold out');
      }
      const buybarPrice = qs('.mobile-buybar__price .price');
      if (buybarPrice) buybarPrice.textContent = formatMoney(variant.price);
      const stock = qs('[data-stock-indicator]');
      const stockText = qs('[data-stock-text]');
      if (stock && stockText) {
        stock.classList.toggle('is-in-stock', variant.available);
        stock.classList.toggle('is-sold-out', !variant.available);
        stockText.textContent = variant.available ? T('inStock', 'In stock — ready to ship') : T('soldOut', 'Sold out');
      }
      const mainImage = qs('#ProductMainImage');
      if (mainImage && variant.featured_image?.src) {
        swapImage(mainImage, variant.featured_image.src);
      }
    };

    // Mark option values that lead to no purchasable variant given the other selections
    const updateOptionStates = () => {
      const selected = getSelectedOptions();
      optionInputs.forEach(input => {
        const idx = parseInt(input.dataset.optionIndex, 10);
        const matches = variants.filter(v =>
          v.options.every((opt, i) => {
            if (i === idx) return opt === input.value;
            return selected[i] ? opt === selected[i] : true;
          })
        );
        const pill = input.closest('.option-pill');
        pill?.classList.toggle('is-unavailable', !matches.some(v => v.available));
      });
    };

    optionInputs.forEach(input => {
      input.addEventListener('change', () => {
        const selected = getSelectedOptions();
        const match = variants.find(v =>
          v.options.every((opt, i) => opt === selected[i])
        );
        applyVariant(match);
        updateOptionStates();
      });
    });
    updateOptionStates();
  },

  bindForm(form) {
    const quantityInput = qs('#Quantity', form);
    qs('[data-quantity-decrease]', form)?.addEventListener('click', () => {
      quantityInput.value = Math.max(1, (parseInt(quantityInput.value, 10) || 1) - 1);
    });
    qs('[data-quantity-increase]', form)?.addEventListener('click', () => {
      quantityInput.value = (parseInt(quantityInput.value, 10) || 1) + 1;
    });

    this.bindVariantSelection(form);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = qs('[data-add-to-cart]', form);
      const originalText = submitBtn?.innerHTML;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = `<span>${escapeHTML(T('adding', 'Adding…'))}</span>`;
      }
      try {
        const formData = new FormData(form);
        formData.append('sections', 'cart-drawer');
        const data = await fetchJSON('/cart/add.js', { method: 'POST', body: formData });
        if (data.sections && data.sections['cart-drawer']) {
          CartDrawer.applySection(data.sections['cart-drawer']);
        } else {
          await CartDrawer.refresh();
        }
        CartDrawer.open();
        Toast.show(T('added', 'Added to cart'));
        trackEvent('add_to_cart', { item_id: formData.get('id'), quantity: formData.get('quantity') || 1 });
      } catch (err) {
        console.error('Add to cart failed', err);
        Toast.show(err.message || T('addError', 'Could not add this item to your cart.'), true);
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalText;
        }
      }
    });
  },

  bindGallery() {
    qsa('[data-product-gallery]').forEach(gallery => {
      const mainImage = qs('#ProductMainImage', gallery);
      qsa('[data-thumb]', gallery).forEach(thumb => {
        thumb.addEventListener('click', () => {
          const url = thumb.getAttribute('data-image-url');
          if (mainImage && url) swapImage(mainImage, url);
          qsa('[data-thumb]', gallery).forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        });
      });
    });
  }
};

/* =========================================================
   Card stepper — the round + becomes − qty + once in cart
   ========================================================= */
const CardStepper = {
  cartMap: {},

  async sync(render = true) {
    try {
      const cart = await fetchJSON('/cart.js');
      this.applyCart(cart, render);
    } catch (e) { /* offline — leave as is */ }
  },

  applyCart(cart, render = true) {
    this.cartMap = {};
    (cart.items || []).forEach(item => {
      this.cartMap[item.variant_id] = (this.cartMap[item.variant_id] || 0) + item.quantity;
    });
    if (render) this.render();
  },

  render(root = document) {
    qsa('[data-card-stepper]', root).forEach(stepper => {
      const id = parseInt(stepper.getAttribute('data-variant-id'), 10);
      const qty = this.cartMap[id] || 0;
      const card = stepper.closest('.product-card');
      const form = card ? qs('.product-card__form', card) : null;
      const inCart = qty > 0;
      stepper.hidden = !inCart;
      if (form) form.hidden = inCart;
      const qtyEl = qs('[data-step-qty]', stepper);
      if (qtyEl && inCart) qtyEl.textContent = qty;
    });
  },

  init(root = document) {
    qsa('[data-card-stepper]', root).forEach(stepper => {
      if (stepper.dataset.bound) return;
      stepper.dataset.bound = 'true';
      qs('[data-step-plus]', stepper)?.addEventListener('click', () => this.change(stepper, 1));
      qs('[data-step-minus]', stepper)?.addEventListener('click', () => this.change(stepper, -1));
    });
  },

  async change(stepper, delta) {
    const id = parseInt(stepper.getAttribute('data-variant-id'), 10);
    const current = this.cartMap[id] || 0;
    const quantity = Math.max(0, current + delta);
    qsa('button', stepper).forEach(b => (b.disabled = true));
    try {
      const cart = await fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ id: String(id), quantity, sections: 'cart-drawer' })
      });
      if (cart.sections && cart.sections['cart-drawer']) {
        CartDrawer.applySection(cart.sections['cart-drawer']);
      }
      this.applyCart(cart);
      if (delta > 0) trackEvent('add_to_cart', { item_id: id, quantity: 1 });
    } catch (err) {
      console.error('Stepper change failed', err);
      Toast.show(err.message || T('addError', 'Could not add this item to your cart.'), true);
    } finally {
      qsa('button', stepper).forEach(b => (b.disabled = false));
    }
  }
};

/* =========================================================
   Product card quick-add (grid contexts)
   ========================================================= */
const QuickAdd = {
  init(root = document) {
    qsa('.product-card__form', root).forEach(form => {
      if (form.dataset.quickAddBound) return;
      form.dataset.quickAddBound = 'true';
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = qs('[data-add-to-cart]', form) || qs('button[type="submit"]', form);
        const isPlus = btn?.classList.contains('product-card__plus');
        const originalText = btn?.textContent;
        if (btn) {
          btn.disabled = true;
          if (isPlus) btn.classList.add('is-loading');
          else btn.textContent = 'Adding…';
        }
        try {
          const formData = new FormData(form);
          formData.append('sections', 'cart-drawer');
          const data = await fetchJSON('/cart/add.js', { method: 'POST', body: formData });
          if (data.sections && data.sections['cart-drawer']) {
            CartDrawer.applySection(data.sections['cart-drawer']);
          } else {
            await CartDrawer.refresh();
          }
          CartDrawer.open();
          Toast.show(T('added', 'Added to cart'));
          trackEvent('add_to_cart', { item_id: formData.get('id'), quantity: 1 });
          CardStepper.sync();
          if (btn && isPlus) {
            btn.classList.add('is-added');
            btn.firstElementChild && (btn.firstElementChild.textContent = '✓');
            setTimeout(() => {
              btn.classList.remove('is-added');
              btn.firstElementChild && (btn.firstElementChild.textContent = '+');
            }, 1600);
          }
        } catch (err) {
          console.error('Quick add failed', err);
          Toast.show(err.message || T('addError', 'Could not add this item to your cart.'), true);
        } finally {
          if (btn) {
            btn.disabled = false;
            if (isPlus) btn.classList.remove('is-loading');
            else btn.textContent = originalText;
          }
        }
      });
    });

    // Round + on multi-variant products opens quick view for option selection
    qsa('[data-plus-quick-view]', root).forEach(btn => {
      if (btn.dataset.plusBound) return;
      btn.dataset.plusBound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.closest('[data-product-handle]')?.getAttribute('data-product-handle');
        if (handle) QuickView.open(handle);
      });
    });
  }
};

/* =========================================================
   Wishlist / compare storage (product handles, localStorage)
   ========================================================= */
const escapeHTML = (str) => String(str).replace(/[&<>"']/g, c => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[c]));

const imageUrl = (src, width) => {
  if (!src) return '';
  return src + (src.includes('?') ? '&' : '?') + 'width=' + width;
};

const ListStore = {
  WISHLIST_KEY: 'muscleseeds:wishlist',
  COMPARE_KEY: 'muscleseeds:compare',

  get(key) {
    try {
      const list = JSON.parse(localStorage.getItem(key) || '[]');
      return Array.isArray(list) ? list.filter(v => typeof v === 'string') : [];
    } catch (e) {
      return [];
    }
  },

  set(key, list) {
    try {
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) { /* storage unavailable, ignore */ }
    if (key === this.WISHLIST_KEY) this.updateWishlistBadge();
    if (key === this.COMPARE_KEY) this.updateCompareFab();
  },

  updateCompareFab() {
    const fab = qs('[data-compare-fab]');
    if (!fab) return;
    const count = this.get(this.COMPARE_KEY).length;
    fab.hidden = count === 0;
    const countEl = qs('[data-compare-count]', fab);
    if (countEl) countEl.textContent = count;
  },

  toggle(key, handle) {
    const list = this.get(key);
    const idx = list.indexOf(handle);
    if (idx > -1) list.splice(idx, 1); else list.push(handle);
    this.set(key, list);
    return idx === -1;
  },

  remove(key, handle) {
    this.set(key, this.get(key).filter(h => h !== handle));
  },

  updateWishlistBadge() {
    const count = this.get(this.WISHLIST_KEY).length;
    qsa('[data-wishlist-count]').forEach(el => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }
};

/* =========================================================
   Product card actions: wishlist, quick view, compare
   ========================================================= */
const ProductCardActions = {
  init(root = document) {
    this.applyStoredState(root);
    ListStore.updateWishlistBadge();
    ListStore.updateCompareFab();

    qsa('[data-wishlist-toggle]', root).forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = this.getHandle(btn);
        if (!handle) return;
        const added = ListStore.toggle(ListStore.WISHLIST_KEY, handle);
        btn.classList.toggle('is-wishlisted', added);
        Toast.show(added ? T('savedWishlist', 'Saved to wishlist') : T('removedWishlist', 'Removed from wishlist'));
      });
    });

    qsa('[data-compare-toggle]', root).forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = this.getHandle(btn);
        if (!handle) return;
        const list = ListStore.get(ListStore.COMPARE_KEY);
        if (!list.includes(handle) && list.length >= 4) {
          Toast.show(T('compareLimit', 'Compare holds up to 4 products'), true);
          return;
        }
        const added = ListStore.toggle(ListStore.COMPARE_KEY, handle);
        btn.classList.toggle('is-comparing', added);
        Toast.show(added ? T('addedCompare', 'Added to compare') : T('removedCompare', 'Removed from compare'));
      });
    });

    qsa('[data-quick-view]', root).forEach(btn => {
      if (btn.dataset.bound) return;
      btn.dataset.bound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = this.getHandle(btn);
        if (handle) QuickView.open(handle);
      });
    });
  },

  getHandle(btn) {
    return btn.closest('[data-product-handle]')?.getAttribute('data-product-handle') || null;
  },

  applyStoredState(root = document) {
    const wishlist = ListStore.get(ListStore.WISHLIST_KEY);
    const compare = ListStore.get(ListStore.COMPARE_KEY);
    qsa('[data-wishlist-toggle]', root).forEach(btn => {
      const handle = this.getHandle(btn);
      if (handle && wishlist.includes(handle)) btn.classList.add('is-wishlisted');
    });
    qsa('[data-compare-toggle]', root).forEach(btn => {
      const handle = this.getHandle(btn);
      if (handle && compare.includes(handle)) btn.classList.add('is-comparing');
    });
  }
};

/* =========================================================
   Quick view modal (Section Rendering API)
   ========================================================= */
const QuickView = {
  modal: null,
  body: null,

  init() {
    this.modal = qs('#QuickViewModal');
    this.body = qs('[data-quick-view-body]');
    if (!this.modal) return;

    qsa('[data-quick-view-close]', this.modal).forEach(el =>
      el.addEventListener('click', () => this.close())
    );
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
  },

  isOpen() { return this.modal?.classList.contains('is-open'); },

  async open(handle) {
    if (!this.modal || !this.body) return;
    this.body.innerHTML = `<p class="quick-view__loading">${escapeHTML(T('loading', 'Loading…'))}</p>`;
    this.modal.classList.add('is-open');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    try {
      const res = await fetch(`/products/${handle}?section_id=quick-view`);
      if (!res.ok) throw new Error('Failed to load product');
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.querySelector('[data-quick-view-content]');
      if (!content) throw new Error('Product unavailable');
      this.body.innerHTML = '';
      this.body.appendChild(content);
      const title = qs('.quick-view__title', this.body)?.textContent.trim();
      if (title) this.modal.setAttribute('aria-label', `${T('quickView', 'Quick view')}: ${title}`);
      this.bindForm();
      FocusTrap.activate(this.modal);
      trackEvent('view_item', { item_handle: handle });
    } catch (err) {
      console.error('Quick view failed', err);
      window.location.href = `/products/${handle}`;
    }
  },

  close() {
    this.modal?.classList.remove('is-open');
    this.modal?.setAttribute('aria-hidden', 'true');
    this.modal?.setAttribute('aria-label', T('quickView', 'Quick view'));
    document.body.style.overflow = '';
    FocusTrap.release();
  },

  bindForm() {
    const form = qs('.quick-view__form', this.body);
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = qs('[data-add-to-cart]', form);
      const originalText = btn?.textContent;
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
      try {
        const formData = new FormData(form);
        formData.append('sections', 'cart-drawer');
        const data = await fetchJSON('/cart/add.js', { method: 'POST', body: formData });
        this.close();
        if (data.sections && data.sections['cart-drawer']) {
          CartDrawer.applySection(data.sections['cart-drawer']);
        } else {
          await CartDrawer.refresh();
        }
        CartDrawer.open();
        Toast.show(T('added', 'Added to cart'));
        trackEvent('add_to_cart', { item_id: formData.get('id'), quantity: 1 });
      } catch (err) {
        console.error('Quick view add failed', err);
        Toast.show(err.message || T('addError', 'Could not add this item to your cart.'), true);
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = originalText; }
      }
    });
  }
};

/* =========================================================
   Wishlist page — renders saved products from /products/{handle}.js
   ========================================================= */
const WishlistPage = {
  async init() {
    const page = qs('[data-wishlist-page]');
    if (!page) return;
    const grid = qs('[data-wishlist-grid]', page);
    const empty = qs('[data-wishlist-empty]', page);
    const handles = ListStore.get(ListStore.WISHLIST_KEY);

    if (!handles.length) return;

    const products = (await Promise.all(
      handles.map(h => fetchJSON(`/products/${h}.js`).catch(() => null))
    )).filter(Boolean);

    if (!products.length) return;
    empty.hidden = true;
    grid.hidden = false;
    grid.innerHTML = products.map(p => this.renderCard(p)).join('');

    qsa('[data-wishlist-remove]', grid).forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.getAttribute('data-wishlist-remove');
        ListStore.remove(ListStore.WISHLIST_KEY, handle);
        btn.closest('.product-card')?.remove();
        if (!qs('.product-card', grid)) {
          grid.hidden = true;
          empty.hidden = false;
        }
      });
    });

    QuickAdd.init(grid);
    CardStepper.init(grid);
    CardStepper.render(grid);
  },

  renderCard(p) {
    const variant = p.variants.find(v => v.available) || p.variants[0];
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const priceHTML = onSale
      ? `<span class="price price--sale">${formatMoney(p.price)}</span>
         <span class="price price--compare">${formatMoney(p.compare_at_price)}</span>`
      : `<span class="price">${formatMoney(p.price)}</span>`;
    const plusHTML = !p.available
      ? `<button type="button" class="product-card__plus" disabled aria-label="${escapeHTML(T('soldOut', 'Sold out'))}"><span aria-hidden="true">+</span></button>`
      : p.variants.length > 1
        ? `<button type="button" class="product-card__plus" data-plus-quick-view aria-label="${escapeHTML(Tn('chooseOptionsFor', `Choose options for ${p.title}`, { TITLE: p.title }))}"><span aria-hidden="true">+</span></button>`
        : `<form class="product-card__form">
             <input type="hidden" name="id" value="${variant.id}">
             <button type="submit" class="product-card__plus" data-add-to-cart aria-label="${escapeHTML(Tn('addNamedToCart', `Add ${p.title} to cart`, { TITLE: p.title }))}"><span aria-hidden="true">+</span></button>
           </form>
           <div class="product-card__stepper" data-card-stepper data-variant-id="${variant.id}" hidden>
             <button type="button" data-step-minus aria-label="${escapeHTML(T('decrease', 'Decrease quantity'))}">−</button>
             <span data-step-qty aria-live="polite">1</span>
             <button type="button" data-step-plus aria-label="${escapeHTML(T('increase', 'Increase quantity'))}">+</button>
           </div>`;

    return `
      <div class="product-card" data-product-handle="${escapeHTML(p.handle)}">
        <a href="${escapeHTML(p.url)}" class="product-card__media">
          ${p.featured_image
            ? `<img src="${escapeHTML(imageUrl(p.featured_image, 600))}" alt="${escapeHTML(p.title)}" loading="lazy">`
            : ''}
          ${!p.available ? `<span class="product-card__badge product-card__badge--soldout">${escapeHTML(T('soldOut', 'Sold out'))}</span>` : ''}
          <div class="product-card__hover-actions" style="opacity:1;transform:none;">
            <button type="button" class="product-card__action-btn" data-wishlist-remove="${escapeHTML(p.handle)}" aria-label="${escapeHTML(Tn('wishlistRemove', `Remove ${p.title} from wishlist`, { TITLE: p.title }))}">&times;</button>
          </div>
        </a>
        <div class="product-card__info">
          ${p.type ? `<p class="product-card__type">${escapeHTML(p.type)}</p>` : ''}
          <h3 class="product-card__title"><a href="${escapeHTML(p.url)}">${escapeHTML(p.title)}</a></h3>
          <div class="product-card__footer">
            <div class="product-card__price">${priceHTML}</div>
            ${plusHTML}
          </div>
        </div>
      </div>`;
  }
};

/* =========================================================
   Compare page — facts-panel-styled comparison table
   ========================================================= */
const ComparePage = {
  async init() {
    const page = qs('[data-compare-page]');
    if (!page) return;
    const wrap = qs('[data-compare-table]', page);
    const empty = qs('[data-compare-empty]', page);
    const handles = ListStore.get(ListStore.COMPARE_KEY);

    if (!handles.length) return;

    const products = (await Promise.all(
      handles.map(h => fetchJSON(`/products/${h}.js`).catch(() => null))
    )).filter(Boolean);

    if (!products.length) return;
    empty.hidden = true;
    wrap.hidden = false;
    wrap.innerHTML = this.renderTable(products);

    qsa('[data-compare-remove]', wrap).forEach(btn => {
      btn.addEventListener('click', () => {
        ListStore.remove(ListStore.COMPARE_KEY, btn.getAttribute('data-compare-remove'));
        window.location.reload();
      });
    });
  },

  renderTable(products) {
    const cells = (render) => products.map(render).join('');
    return `
      <table class="compare-table">
        <thead>
          <tr>
            <th scope="col"><span class="visually-hidden">Attribute</span></th>
            ${cells(p => `
              <th scope="col">
                <a href="${escapeHTML(p.url)}" class="compare-table__media">
                  ${p.featured_image ? `<img src="${escapeHTML(imageUrl(p.featured_image, 400))}" alt="${escapeHTML(p.title)}" loading="lazy">` : ''}
                </a>
                <a href="${escapeHTML(p.url)}" class="compare-table__title">${escapeHTML(p.title)}</a>
                <button type="button" class="compare-table__remove" data-compare-remove="${escapeHTML(p.handle)}">${escapeHTML(T('remove', 'Remove'))}</button>
              </th>`)}
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope="row">${escapeHTML(T('category', 'Category'))}</th>
            ${cells(p => `<td>${escapeHTML(p.type || '—')}</td>`)}
          </tr>
          <tr>
            <th scope="row">${escapeHTML(T('price', 'Price'))}</th>
            ${cells(p => `<td class="compare-table__price">${formatMoney(p.price)}</td>`)}
          </tr>
          <tr>
            <th scope="row">${escapeHTML(T('availability', 'Availability'))}</th>
            ${cells(p => `<td>${p.available ? `<span class="compare-table__instock">${escapeHTML(T('inStockShort', 'In stock'))}</span>` : escapeHTML(T('soldOut', 'Sold out'))}</td>`)}
          </tr>
          <tr>
            <th scope="row">${escapeHTML(T('details', 'Details'))}</th>
            ${cells(p => {
              const text = (p.description || '').replace(/<[^>]*>/g, '').trim();
              return `<td>${escapeHTML(text.length > 140 ? text.slice(0, 140) + '…' : text || '—')}</td>`;
            })}
          </tr>
          <tr>
            <th scope="row"></th>
            ${cells(p => `<td><a href="${escapeHTML(p.url)}" class="btn btn--block">${escapeHTML(T('viewProduct', 'View product'))}</a></td>`)}
          </tr>
        </tbody>
      </table>`;
  }
};

/* =========================================================
   Related products (Section Rendering API)
   ========================================================= */
const RelatedProducts = {
  init() {
    qsa('[data-product-recommendations]').forEach(async (section) => {
      const url = section.getAttribute('data-url');
      const target = qs('[data-recommendations-target]', section);
      if (!url || !target) return;
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const html = await res.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newTarget = doc.querySelector('[data-recommendations-target]');
        if (newTarget && newTarget.children.length) {
          target.replaceWith(newTarget);
          if (section.hasAttribute('data-hide-when-empty')) section.hidden = false;
          QuickAdd.init(section);
          ProductCardActions.init(section);
        }
      } catch (err) {
        console.error('Failed to load recommendations', err);
      }
    });
  }
};

/* =========================================================
   PDP extras: sticky mobile buy bar, lightbox, video, stack add
   ========================================================= */
const ProductExtras = {
  init() {
    this.buyBar();
    this.lightbox();
    this.videoThumbs();
    this.stackAdd();
  },

  buyBar() {
    const bar = qs('[data-mobile-buybar]');
    const submit = qs('.product-form__submit');
    if (!bar || !submit || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries[0].isIntersecting;
      bar.classList.toggle('is-visible', !visible);
      bar.setAttribute('aria-hidden', String(visible));
    }, { threshold: 0 });
    observer.observe(submit);
  },

  lightbox() {
    const box = qs('[data-lightbox]');
    if (!box) return;
    const track = qs('[data-lightbox-track]', box);
    const dots = qsa('[data-lightbox-dots] span', box);
    const mainImage = qs('#ProductMainImage');

    const open = () => {
      box.classList.add('is-open');
      box.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      FocusTrap.activate(box);
    };
    const close = () => {
      box.classList.remove('is-open');
      box.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      FocusTrap.release();
    };

    qsa('[data-lightbox-open]').forEach(btn => btn.addEventListener('click', open));
    mainImage?.addEventListener('click', open);
    qsa('[data-lightbox-close]', box).forEach(btn => btn.addEventListener('click', close));
    box.addEventListener('click', (e) => { if (e.target === box || e.target === track) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && box.classList.contains('is-open')) close();
    });

    if (track && dots.length) {
      track.addEventListener('scroll', () => {
        const index = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach((d, i) => d.classList.toggle('is-active', Math.abs(i) === Math.abs(index)));
      }, { passive: true });
    }
  },

  videoThumbs() {
    const video = qs('[data-product-video]');
    const mainImage = qs('#ProductMainImage');
    if (!video || !mainImage) return;
    qsa('[data-thumb-video]').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const url = thumb.getAttribute('data-video-url');
        if (!url) return;
        video.src = url;
        video.hidden = false;
        mainImage.hidden = true;
        video.play().catch(() => {});
        qsa('[data-thumb], [data-thumb-video]').forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
    // returning to an image thumb restores the image
    qsa('[data-thumb]').forEach(thumb => {
      thumb.addEventListener('click', () => {
        video.pause();
        video.hidden = true;
        mainImage.hidden = false;
      });
    });
  },

  stackAdd() {
    qsa('[data-add-stack]').forEach(btn => {
      btn.addEventListener('click', async () => {
        const ids = (btn.getAttribute('data-stack-ids') || '').split(',').filter(Boolean);
        if (!ids.length) return;
        btn.disabled = true;
        try {
          const data = await fetchJSON('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify({
              items: ids.map(id => ({ id: parseInt(id, 10), quantity: 1 })),
              sections: 'cart-drawer'
            })
          });
          if (data.sections && data.sections['cart-drawer']) {
            CartDrawer.applySection(data.sections['cart-drawer']);
          } else {
            await CartDrawer.refresh();
          }
          CartDrawer.open();
          Toast.show(T('addedBoth', 'Stack added to cart'));
          ids.forEach(id => trackEvent('add_to_cart', { item_id: id, quantity: 1 }));
        } catch (err) {
          console.error('Stack add failed', err);
          Toast.show(err.message || T('addError', 'Could not add this item to your cart.'), true);
        } finally {
          btn.disabled = false;
        }
      });
    });
  }
};

/* =========================================================
   Hero fact card cycler — prev/next + auto-advance
   ========================================================= */
const FactCycler = {
  init() {
    qsa('[data-fact-cycler]').forEach(card => {
      const facts = qsa('.hero-card__fact', card);
      if (facts.length < 2) return;
      let index = 0;
      let timer = null;

      const show = (i) => {
        facts[index].classList.remove('is-active');
        index = (i + facts.length) % facts.length;
        facts[index].classList.add('is-active');
      };
      const restart = () => {
        clearInterval(timer);
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          timer = setInterval(() => show(index + 1), 5000);
        }
      };

      qs('[data-cycle-prev]', card)?.addEventListener('click', () => { show(index - 1); restart(); });
      qs('[data-cycle-next]', card)?.addEventListener('click', () => { show(index + 1); restart(); });
      restart();
    });
  }
};

/* =========================================================
   Mascot rotator — crossfades between hero mascot poses
   ========================================================= */
const MascotRotator = {
  init() {
    qsa('[data-mascot-rotator]').forEach(rotator => {
      const slides = qsa('.hero__mascot-slide', rotator);
      if (slides.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      let index = 0;
      setInterval(() => {
        slides[index].classList.remove('is-active');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
      }, 5000);
    });
  }
};

/* =========================================================
   Recently viewed — records PDP visits, renders a card rail
   ========================================================= */
const RecentlyViewed = {
  KEY: 'muscleseeds:recent',
  MAX: 8,

  init() {
    this.record();
    this.render();
  },

  record() {
    const handle = qs('[data-product-page-handle]')?.getAttribute('data-product-page-handle');
    if (!handle) return;
    let list = ListStore.get(this.KEY);
    list = [handle, ...list.filter(h => h !== handle)].slice(0, this.MAX);
    try {
      localStorage.setItem(this.KEY, JSON.stringify(list));
    } catch (e) { /* storage unavailable */ }
  },

  async render() {
    const section = qs('[data-recently-viewed]');
    if (!section) return;
    const rail = qs('[data-recently-viewed-rail]', section);
    const currentHandle = qs('[data-product-page-handle]')?.getAttribute('data-product-page-handle');
    const handles = ListStore.get(this.KEY).filter(h => h !== currentHandle);
    if (!handles.length) return;

    const products = (await Promise.all(
      handles.map(h => fetchJSON(`/products/${h}.js`).catch(() => null))
    )).filter(Boolean);
    if (!products.length) return;

    rail.innerHTML = products.map(p => WishlistPage.renderCard(p)).join('');
    // strip the wishlist-remove control this rail inherits from the shared card renderer
    qsa('[data-wishlist-remove]', rail).forEach(btn => btn.closest('.product-card__hover-actions')?.remove());
    section.hidden = false;
    QuickAdd.init(rail);
    CardStepper.init(rail);
    CardStepper.render(rail);
  }
};

/* =========================================================
   Scroll reveal
   ========================================================= */
const ScrollReveal = {
  init() {
    const targets = qsa('[data-reveal]');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(el => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });
    targets.forEach(el => observer.observe(el));
  }
};

/* =========================================================
   Offers page — copy a discount code to the clipboard
   ========================================================= */
const OfferCodes = {
  init() {
    const buttons = qsa('[data-copy-code]');
    if (!buttons.length) return;
    buttons.forEach(btn => {
      btn.addEventListener('click', async () => {
        const code = btn.dataset.copyCode;
        const action = qs('.offer-card__code-action', btn);
        const original = action ? action.textContent : '';
        try {
          await navigator.clipboard.writeText(code);
        } catch (e) {
          // Clipboard API needs a secure context — fall back to a selection copy
          const tmp = document.createElement('textarea');
          tmp.value = code;
          tmp.setAttribute('readonly', '');
          tmp.style.position = 'absolute';
          tmp.style.left = '-9999px';
          document.body.appendChild(tmp);
          tmp.select();
          try { document.execCommand('copy'); } catch (err) { /* noop */ }
          document.body.removeChild(tmp);
        }
        Toast.show(Tn('codeCopied', 'Code [CODE] copied', { CODE: code }));
        if (action) {
          btn.classList.add('is-copied');
          action.textContent = T('copied', 'Copied');
          setTimeout(() => {
            btn.classList.remove('is-copied');
            action.textContent = original;
          }, 2000);
        }
      });
    });
  }
};

/* =========================================================
   Distributor page — quantity request rows
   Blank/zero boxes are disabled on submit so they never reach
   the notification email. Without JS they simply arrive empty.
   ========================================================= */
const QuantityRequest = {
  init() {
    const fields = qsa('[data-qty-field]');
    if (!fields.length) return;
    const form = fields[0].closest('form');
    if (!form) return;
    form.addEventListener('submit', () => {
      fields.forEach(field => {
        const value = parseInt(field.value, 10);
        field.disabled = !value || value <= 0;
      });
    });
  }
};

/* =========================================================
   Slide carousel — shared by the home hero and the offer slides.
   A native scroll-snap track (so swipe and keyboard scrolling work
   with no JS at all) plus arrows, dots and optional autoplay.
   Position is measured with getBoundingClientRect deltas rather
   than scrollLeft, because scrollLeft's sign and origin differ
   between browsers in RTL.
   ========================================================= */
const SlideCarousel = {
  init() {
    qsa('[data-carousel]').forEach(root => {
      const track = qs('[data-carousel-track]', root);
      const slides = qsa('.js-slide', root);
      if (!track || slides.length < 2) return;

      const dots = qsa('[data-slide-index]', root);
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      let index = 0;
      let timer = null;

      const currentIndex = () => {
        const left = track.getBoundingClientRect().left;
        let best = 0;
        let min = Infinity;
        slides.forEach((slide, i) => {
          const delta = Math.abs(slide.getBoundingClientRect().left - left);
          if (delta < min) { min = delta; best = i; }
        });
        return best;
      };

      const paint = () => {
        index = currentIndex();
        dots.forEach((dot, i) => {
          const active = i === index;
          dot.classList.toggle('is-active', active);
          if (active) { dot.setAttribute('aria-current', 'true'); }
          else { dot.removeAttribute('aria-current'); }
        });
      };

      const goTo = (i) => {
        const target = slides[(i + slides.length) % slides.length];
        const delta = target.getBoundingClientRect().left - track.getBoundingClientRect().left;
        track.scrollBy({ left: delta, behavior: reduced ? 'auto' : 'smooth' });
      };

      qs('[data-carousel-prev]', root)?.addEventListener('click', () => { goTo(index - 1); stop(); });
      qs('[data-carousel-next]', root)?.addEventListener('click', () => { goTo(index + 1); stop(); });
      dots.forEach(dot => {
        dot.addEventListener('click', () => { goTo(Number(dot.dataset.slideIndex)); stop(); });
      });

      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => { paint(); ticking = false; });
      }, { passive: true });

      function start() {
        const seconds = Number(root.dataset.autoplay);
        if (!seconds || reduced) return;
        stop();
        timer = setInterval(() => {
          if (document.hidden) return;
          goTo(index + 1);
        }, seconds * 1000);
      }
      function stop() { clearInterval(timer); timer = null; }

      if (root.dataset.autoplay) {
        root.addEventListener('mouseenter', stop);
        root.addEventListener('mouseleave', start);
        root.addEventListener('focusin', stop);
        root.addEventListener('focusout', start);
        start();
      }

      paint();
    });
  }
};

/* =========================================================
   Gift offers — rules come from the "Gift offers" section, so
   codes, gift products and thresholds are all editor-managed.

   The script only puts a gift IN the cart; the matching Shopify
   discount is what makes it free at checkout. That is why a gift is
   pulled back out when its rule stops qualifying — otherwise the
   discount fails and the customer is charged for it.
   ========================================================= */
const GiftOffers = {
  offers: [],
  currency: 'EGP',
  busy: false,
  hadGiftFor: null,

  init() {
    const el = qs('#GiftOffersConfig');
    if (!el) return;
    let cfg;
    try { cfg = JSON.parse(el.textContent); } catch (e) { return; }
    this.offers = (cfg.offers || []).filter(o => o.variantId && o.thresholdCents);
    this.currency = cfg.currency || this.currency;
    if (!this.offers.length) return;
    this.check();
  },

  offerFor(code) {
    const wanted = String(code || '').trim().toUpperCase();
    return this.offers.find(o => o.code && o.code.toUpperCase() === wanted) || null;
  },

  isGiftItem(item) {
    return (item.properties && item.properties._gift_offer) ||
           this.offers.some(o => o.variantId === item.variant_id);
  },

  /* gifts never count toward a threshold — otherwise a qualifying cart stays
     qualifying purely because the gift is in it, and no gift can be removed */
  eligibleSubtotal(cart) {
    return cart.items.reduce((sum, item) => (
      this.isGiftItem(item) ? sum : sum + item.final_line_price
    ), 0);
  },

  /* Shopify takes one discount code per checkout, so pick a single winner:
     the richest threshold the cart has actually reached */
  bestOffer(subtotal, typedCode) {
    return this.offers
      .filter(o => subtotal >= o.thresholdCents)
      .filter(o => o.autoAdd || (typedCode && o.code.toUpperCase() === typedCode.toUpperCase()))
      .sort((a, b) => b.thresholdCents - a.thresholdCents)[0] || null;
  },

  money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: this.currency, maximumFractionDigits: 0
      }).format(cents / 100);
    } catch (e) {
      return (cents / 100).toFixed(0);
    }
  },

  dismissed(id) {
    try { return sessionStorage.getItem('msGiftOff:' + id) === '1'; } catch (e) { return false; }
  },
  setDismissed(id, value) {
    try {
      if (value) sessionStorage.setItem('msGiftOff:' + id, '1');
      else sessionStorage.removeItem('msGiftOff:' + id);
    } catch (e) { /* private mode — the gift simply returns next time */ }
  },

  /* how much more is needed for the nearest offer the cart has not reached */
  shortfallFor(offer, subtotal) {
    return Math.max(0, offer.thresholdCents - subtotal);
  },

  async check(typedCode) {
    if (this.busy || !this.offers.length) return;
    this.busy = true;
    let changed = false;

    try {
      const cart = await fetchJSON('/cart.js');
      const subtotal = this.eligibleSubtotal(cart);
      const winner = this.bestOffer(subtotal, typedCode);

      // drop any gift that is not the winning one (rule expired, cart fell,
      // or a richer tier took over)
      for (let i = cart.items.length - 1; i >= 0; i--) {
        const item = cart.items[i];
        if (!this.isGiftItem(item)) continue;
        if (winner && item.variant_id === winner.variantId) continue;
        await fetchJSON('/cart/change.js', {
          method: 'POST',
          body: JSON.stringify({ line: i + 1, quantity: 0 })
        });
        Toast.show(Tn('freeGiftRemoved', 'Free [PRODUCT] removed — your cart is below the offer minimum.', { PRODUCT: item.product_title }));
        changed = true;
      }

      if (winner) {
        const fresh = changed ? await fetchJSON('/cart.js') : cart;
        const line = fresh.items.findIndex(i => i.variant_id === winner.variantId);

        if (line > -1) {
          this.hadGiftFor = winner.id;
          if (fresh.items[line].quantity > 1) {
            await fetchJSON('/cart/change.js', {
              method: 'POST',
              body: JSON.stringify({ line: line + 1, quantity: 1 })
            });
            changed = true;
          }
        } else if (this.hadGiftFor === winner.id) {
          // it was there and the cart still qualifies — the shopper removed it,
          // so respect that rather than re-adding in a loop
          this.setDismissed(winner.id, true);
          this.hadGiftFor = null;
        } else if (!this.dismissed(winner.id)) {
          await fetchJSON('/cart/add.js', {
            method: 'POST',
            body: JSON.stringify({
              items: [{ id: winner.variantId, quantity: 1, properties: { _gift_offer: winner.id } }]
            })
          });
          if (winner.code) {
            try { await fetch(`/discount/${encodeURIComponent(winner.code)}`); } catch (e) { /* non-fatal */ }
          }
          Toast.show(Tn('freeGiftAdded', 'Added your free [PRODUCT]', { PRODUCT: winner.title }));
          this.hadGiftFor = winner.id;
          changed = true;
        }
      } else {
        this.hadGiftFor = null;
        this.offers.forEach(o => this.setDismissed(o.id, false));
      }
    } catch (err) {
      console.error('Gift offer check failed', err);
    } finally {
      this.busy = false;
    }

    if (changed && typeof CartDrawer !== 'undefined') {
      await CartDrawer.refresh();
      if (qs('.cart-page')) window.location.reload();
    }
  },

  /* Called when a code is typed. Returns a message when the code belongs to a
     gift rule the cart has not qualified for yet, so the shopper is told what
     to do instead of being promised a discount that will silently fail. */
  async messageForCode(code) {
    const offer = this.offerFor(code);
    if (!offer) return null;
    try {
      const cart = await fetchJSON('/cart.js');
      const short = this.shortfallFor(offer, this.eligibleSubtotal(cart));
      if (short > 0) {
        return Tn('giftShortfall', 'Add [AMOUNT] more to unlock this offer.', { AMOUNT: this.money(short) });
      }
    } catch (e) { /* fall through — the code still reaches checkout */ }
    return null;
  }
};

/* =========================================================
   Init on DOM ready
   ========================================================= */
document.addEventListener('DOMContentLoaded', () => {
  Toast.init();
  CartDrawer.init();
  MobileMenu.init();
  HeaderSearch.init();
  PredictiveSearch.init();
  CollectionFilters.init();
  ProductForm.init();
  QuickAdd.init();
  CardStepper.init();
  CardStepper.sync();
  QuickView.init();
  ProductCardActions.init();
  RelatedProducts.init();
  WishlistPage.init();
  ComparePage.init();
  RecentlyViewed.init();
  MascotRotator.init();
  FactCycler.init();
  ProductExtras.init();
  ScrollReveal.init();
  ProductTabs.init();
  OfferCodes.init();
  SlideCarousel.init();
  QuantityRequest.init();
  GiftOffers.init();

  const cartPage = qs('.cart-page');
  if (cartPage) CartDrawer.bindLineItemControls(cartPage);
});
